import React, { useCallback, useState } from 'react';
import { Popover } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ticket as ticketPath } from '../../../router/roleWisePageRoute';
import { isIndividualMilestoneStatusInRange, MilestoneSubStatus, TicketMilestoneEvents, TicketStatus } from '../TicketController';
import IconToolTip from '../../../common/IconToolTip';
import { canApproveTicketAmount, isCreateTicketAllowed } from '../../../utils/access';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { openCustomModal } from '../../../actions/modal';
import AddMilestoneInServiceRequest from '../Modal/AddMilestoneInServiceRequest';
import { getLookupWitHDefaultValue } from '../../../utils/common';
import { SimpleButton } from '../../../common/Buttons/SimpleButton';
import { openChatModal } from '../../../actions/chat';
import PavAssetVerification from '../../../features/pav/PavAssetVerification';
import { getPavMilestoneLabel, isPavMilestone } from '../../../features/pav/pavConfig';

export const TicketStatusOrder = getLookupWitHDefaultValue({
    lookup: {
        OPEN: 'bg-green-100 border border-green-400 text-green-800',
        TRIGGERED: 'bg-blue-100 border border-blue-400 text-blue-800',
        PLANNED: 'bg-amber-100 border border-amber-400 text-amber-800',
        IN_PROGRESS: 'bg-yellow-100 border border-yellow-400 text-yellow-800',
        ON_HOLD: 'bg-gray-100 border border-gray-400 text-gray-800',
        SIGNOFF_REQUESTED: 'bg-purple-100 border border-purple-400 text-purple-800',
        CLOSED: 'bg-red-100 border border-red-400 text-red-800',
        REJECTED: 'bg-red-100 border border-red-400 text-red-800',
        COMPLETED: 'bg-green-50 borde border-green-600'
    },
    defaultValue: 'bg-green-100 border border-green-400 text-green-800'
})


export const MilestoneStatusLabels = getLookupWitHDefaultValue({
    lookup: {
        NEW: { color: 'text-blue-700', label: 'New' },
        REMOTE_ASSIGNED: { color: 'text-blue-700', label: 'Remote Assigned' },
        FIELD_ASSIGNED: { color: 'text-amber-700', label: 'Field Assigned' },
        BROADCASTED: { color: 'text-yellow-700', label: 'Broadcasted' },
        FIELD_ACCEPTED: { color: 'text-indigo-700', label: 'Field Accepted' },
        TRACKING_REQUESTED: { color: 'text-purple-700', label: 'Tracking Requested' },
        ENGINEER_ON_THE_WAY: { color: 'text-blue-800', label: 'Engineer On The Way' },
        ENGINEER_AT_SITE: { color: 'text-yellow-800', label: 'Engineer At Site' },
        ENGINEER_LEFT_SITE: { color: 'text-orange-800', label: 'Engineer Left Site' },
        SITE_COMPLETED: { color: 'text-green-800', label: 'Site Completed' },
        SIGN_OFF_REQUESTED: { color: 'text-purple-800', label: 'Sign Off Requested' },
        CUSTOMER_SIGN_OFF_ACCEPTED: { color: 'text-emerald-800', label: 'Customer Sign Off Accepted' },
        CUSTOMER_SIGN_OFF_REJECTED: { color: 'text-red-800', label: 'Customer Sign Off Rejected' },
        SIGN_OFF_REJECTED: { color: 'text-red-800', label: 'Sign Off Rejected' },
        SIGN_OFF_ACCEPTED: { color: 'text-emerald-800', label: 'Sign Off Accepted' },
        HARD_CLOSED: { color: 'text-red-800', label: 'Hard Closed' },
        SIGN_OFF_LOCKED: { color: 'text-gray-800', label: 'Sign Off Locked' },
        OPEN: { color: 'text-green-800', label: 'Open' },
        TRIGGERED: { color: 'text-blue-800', label: 'Triggered' },
        PLANNED: { color: 'text-amber-800', label: 'Planned' },
        IN_PROGRESS: { color: 'text-yellow-800', label: 'In Progress' },
        ON_HOLD: { color: 'text-gray-800', label: 'On Hold' },
        SIGNOFF_REQUESTED: { color: 'text-purple-800', label: 'Signoff Requested' },
        CLOSED: { color: 'text-red-800', label: 'Closed' },
        HOLD: { color: 'text-gray-800', label: 'Hold' },
    },
    defaultValue: { color: 'text-blue-700', label: 'New' },
})

export const getStatusLabel = (statusKey) => {
    return MilestoneStatusLabels[statusKey]?.label || statusKey;
};

export const MilestoneSubStatusColor = getLookupWitHDefaultValue({
    lookup: {
        NEW: 'border border-blue-300 text-blue-700 bg-blue-50 text-font08 px-p-3 rounded-3px border-l-4 border-l-blue-500',
        REMOTE_ASSIGNED: 'border border-blue-300 text-blue-700 bg-blue-50 text-font08 px-p-3 rounded-3px border-l-4 border-l-blue-500',
        FIELD_ASSIGNED: 'border border-amber-300 text-amber-700 bg-amber-50 text-font08 px-p-3 rounded-3px border-l-4 border-l-amber-500',
        BROADCASTED: 'border border-yellow-300 text-yellow-700 bg-yellow-50 text-font08 px-p-3 rounded-3px border-l-4 border-l-yellow-500',
        FIELD_ACCEPTED: 'border border-indigo-300 text-indigo-700 bg-indigo-50 text-font08 px-p-3 rounded-3px border-l-4 border-l-indigo-500',

        // Tracking/progress states - yellow/orange family
        TRACKING_REQUESTED: 'border border-purple-300 text-purple-700 bg-purple-50 text-font08 px-p-3 rounded-3px border-l-4 border-l-purple-500',
        ENGINEER_ON_THE_WAY: 'border border-blue-400 text-blue-800 bg-blue-50 text-font08 px-p-3 rounded-3px border-l-4 border-l-blue-600',
        ENGINEER_AT_SITE: 'border border-yellow-400 text-yellow-800 bg-yellow-50 text-font08 px-p-3 rounded-3px border-l-4 border-l-yellow-600',
        ENGINEER_LEFT_SITE: 'border border-orange-400 text-orange-800 bg-orange-50 text-font08 px-p-3 rounded-3px border-l-4 border-l-orange-600',

        // Completion states - green/purple family
        SITE_COMPLETED: 'border border-green-400 text-green-800 bg-green-50 text-font08 px-p-3 rounded-3px border-l-4 border-l-green-600',
        SIGN_OFF_REQUESTED: 'border border-purple-400 text-purple-800 bg-purple-50 text-font08 px-p-3 rounded-3px border-l-4 border-l-purple-600 cursor-pointer',
        CUSTOMER_SIGN_OFF_ACCEPTED: 'border border-emerald-400 text-emerald-800 bg-emerald-50 text-font08 px-p-3 rounded-3px border-l-4 border-l-emerald-600 cursor-pointer',
        CUSTOMER_SIGN_OFF_REJECTED: 'border border-red-400 text-red-800 bg-red-50 text-font08 px-p-3 rounded-3px border-l-4 border-l-red-600 cursor-pointer',
        SIGN_OFF_REJECTED: 'border border-red-400 text-red-800 bg-red-50 text-font08 px-p-3 rounded-3px border-l-4 border-l-red-600',
        SIGN_OFF_ACCEPTED: 'border border-emerald-400 text-emerald-800 bg-emerald-50 text-font08 px-p-3 rounded-3px border-l-4 border-l-emerald-600 cursor-pointer',

        // Closed state - red family
        HARD_CLOSED: 'border border-red-500 text-red-800 bg-red-50 text-font08 px-p-3 rounded-3px border-l-4 border-l-red-700',

        // Signoff Locked state - gray family
        SIGN_OFF_LOCKED: 'border border-gray-400 text-gray-800 bg-gray-50 text-font08 px-p-3 rounded-3px border-l-4 border-l-gray-700',

        // Ticket Status - with consistent left border colors matching status meaning
        OPEN: 'bg-green-100 border border-green-400 text-green-800 text-font08 px-p-3 rounded-3px border-l-4 border-l-green-700', // Green for new/open
        TRIGGERED: 'bg-blue-100 border border-blue-400 text-blue-800 text-font08 px-p-3 rounded-3px border-l-4 border-l-blue-700', // Blue for triggered
        PLANNED: 'bg-amber-100 border border-amber-400 text-amber-800 text-font08 px-p-3 rounded-3px border-l-4 border-l-amber-700', // Amber for planned
        IN_PROGRESS: 'bg-yellow-100 border border-yellow-400 text-yellow-800 text-font08 px-p-3 rounded-3px border-l-4 border-l-yellow-700', // Yellow for in progress
        ON_HOLD: 'bg-gray-100 border border-gray-400 text-gray-800 text-font08 px-p-3 rounded-3px border-l-4 border-l-gray-700', // Gray for on hold
        SIGNOFF_REQUESTED: 'bg-purple-100 border border-purple-400 text-purple-800 text-font08 px-p-3 rounded-3px border-l-4 border-l-purple-700', // Purple for signoff
        CLOSED: 'bg-red-100 border border-red-400 text-red-800 text-font08 px-p-3 rounded-3px border-l-4 border-l-red-700', // Red for closed
        REJECTED: 'bg-red-100 border border-red-400 text-red-800 text-font08 px-p-3 rounded-3px border-l-4 border-l-red-700',
        COMPLETED: 'border border-emerald-400 text-emerald-800 bg-emerald-50 text-font08 px-p-3 rounded-3px border-l-4 border-l-emerald-600',
    },
    defaultValue: 'border border-blue-300 text-blue-700 bg-blue-50 text-font08 px-p-3 rounded-3px border-l-4 border-l-blue-500',
})

export const StatusColumn = ({ milestones, ticket_id, ticket }) => {
    const visibleMilestoneCount = 3;
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [milestonesAnchorEl, setMilestonesAnchorEl] = useState(null);

    const onAddNewMilestoneClick = () => {
        dispatch(
            openCustomModal({
                heading: "Add New Milstone",
                modalWidth: '60rem',
                modalHeight: 'auto',
                modalComponent: <AddMilestoneInServiceRequest serviceRequest={ticket} org_id={ticket.org_id} project_id={ticket.project_id} />,
            })
        );
    };

    const getSignOffRequestedDate = (milestone) => {
        let date = undefined;
        if (ticket.remarks) {
            date = ticket.remarks.find(remark => remark.action === TicketMilestoneEvents.MILESTONE_SIGN_OFF_REQUESTED && remark.milestone_id === milestone.id)?.created_at;
        }
        return date;
    }

    const isSignOffRequestHighlighted = (milestone) => {
        const signOffDate = getSignOffRequestedDate(milestone);
        if (!signOffDate) return false;

        const daysDiff = Math.floor((new Date() - new Date(signOffDate)) / (1000 * 60 * 60 * 24));
        return daysDiff >= 3 && daysDiff <= 7;
    }

    const onOpenChatCLick = ({ ticketId, buttonName, isRightClick = false }) => {
        dispatch(openChatModal({ chatId: ticketId, buttonName, isRightClick, formName: `open_chat_${ticketId}`, defaulOpentChecklist: true }));
    };

    const openPavVerification = (milestone) => {
        const ticketNumber = ticket?.ticket_number || ticket?.job_ticket_number;
        dispatch(openCustomModal({
            heading: (
                <span className='text-black font-semibold'>
                    {getPavMilestoneLabel(milestone)}
                    {ticketNumber ? <span className='text-scogobgsky font-semibold'>: {ticketNumber}</span> : ''}
                </span>
            ),
            modalWidth: 'min(82rem, 95vw)',
            modalHeight: 'auto',
            maxHeight: '78vh',
            modalComponent: <PavAssetVerification ticket={ticket} milestone={milestone} variant='modal' />,
        }));
    };

    const memoizedStatus = useCallback((milestone) => {
        if (!milestone || !ticket) return { milestoneTooltipText: '', milestoneStatus: '' };
        let milestoneStatus = getStatusLabel(milestone.milestone_status);
        let milestoneTooltipText = `Milestone Status: ${milestoneStatus}${milestone.is_signoff_locked ? ' (Locked)' : ''} `;

        if (milestone.milestone_status === MilestoneSubStatus.SIGN_OFF_REJECTED) {
            milestoneStatus = getStatusLabel(MilestoneSubStatus.SIGN_OFF_REJECTED);
            const findSignOffRejectRemarks = ticket.remarks && ticket.remarks.find(remark => remark.action === TicketMilestoneEvents.MILESTONE_SIGN_OFF_REJECTED);
            if (findSignOffRejectRemarks) {
                milestoneTooltipText = `${milestoneTooltipText} \n Remark: ${findSignOffRejectRemarks.remark}`;
            }
        }

        const isOnHold = ticket.status === TicketStatus.ON_HOLD;
        const findHoldRemarks = ticket.remarks && ticket.remarks.find(remark => remark.action === TicketMilestoneEvents.HOLD);

        if (isOnHold && findHoldRemarks) {
            milestoneTooltipText = `${milestoneTooltipText} \n Ticket Hold Remark: ${findHoldRemarks.remark}`;
        }

        if (ticket.is_customer_sign_off_required === true && milestone.milestone_status === MilestoneSubStatus.SIGN_OFF_REQUESTED) {
            milestoneTooltipText = `${milestoneTooltipText} \n This milestone is pending customer sign-off.`;
        }
        return { milestoneTooltipText, milestoneStatus };
    }, [ticket]);

    const getMilestoneAgents = (milestoneId) => {
        const agents = Array.isArray(ticket?.agents) ? ticket.agents : [];
        const sp = agents.find((agent) => agent.role === 'SP' && agent.milestone_id === milestoneId);
        const fe = agents.find((agent) => agent.role === 'FE' && agent.milestone_id === milestoneId);
        const getAgentName = (assignment) => assignment?.agent?.display_name
            || assignment?.agent?.full_name
            || assignment?.agent?.name
            || 'Not Assigned';

        return {
            sp,
            fe,
            spName: getAgentName(sp),
            feName: getAgentName(fe),
            isSamePerson: Boolean(sp && fe && sp.agent?.id === fe.agent?.id)
        };
    };

    const renderMilestone = (milestone, { inPopover = false } = {}) => {
        let { milestone_status, type } = milestone;
        if (ticket.status === TicketStatus.ON_HOLD) {
            milestone_status = TicketStatus.ON_HOLD;
        }

        const { sp, fe, spName, feName, isSamePerson } = getMilestoneAgents(milestone.id);
        const agentTooltip = isSamePerson
            ? <>SP/FE: <strong>{spName}</strong></>
            : (
                <>
                    SP: <strong>{sp ? spName : 'Not Assigned'}</strong>
                    <br />
                    FE: <strong>{fe ? feName : 'Not Assigned'}</strong>
                </>
            );
        const tooltipContent = (
            <span>
                <span className='whitespace-pre-line'>{memoizedStatus(milestone).milestoneTooltipText}</span>
                <br />
                {agentTooltip}
            </span>
        );
        const highlightClasses = isSignOffRequestHighlighted(milestone) ? 'shadow-lg animate-pulse max-w-max max-h-max' : '';
        const milestoneContent = (
            <IconToolTip title={tooltipContent}>
                <div className="relative flex items-center min-w-0">
                    <span className={`max-w-9rem truncate p-1 items-center capitalize ${MilestoneSubStatusColor[milestone_status]} font-medium py-1 pr-2 flex gap-1`}>
                        {`${type?.replace(/_/g, ' ')}: ${memoizedStatus(milestone).milestoneStatus}`}
                        {milestone.is_signoff_locked && <span className="material-icons text-gray-600 text-font12">lock</span>}
                    </span>
                </div>
            </IconToolTip>
        );

        return (
            <div
                key={milestone.id}
                onClick={(event) => {
                    if (isPavMilestone(milestone)) {
                        event.stopPropagation();
                        setMilestonesAnchorEl(null);
                        openPavVerification(milestone);
                        return;
                    }
                    const approveReject = milestone && isIndividualMilestoneStatusInRange(milestone, MilestoneSubStatus.SIGN_OFF_REQUESTED);
                    if (approveReject && canApproveTicketAmount(newLoggedUserData.actions)) {
                        navigate(`${ticketPath.path}/${ticket_id}/edit-payout?source=ticket?milestone_id=${milestone.id}`)
                    }
                }}
                className={`min-w-0 milestone_status_${type} ${isPavMilestone(milestone) ? 'cursor-pointer' : ''} ${milestone.is_signoff_locked ? 'relative' : ''} ${highlightClasses} ${inPopover ? 'grid w-full grid-cols-[minmax(0,1fr)_minmax(8rem,0.75fr)] items-center gap-3 rounded-lg border border-gray-100 bg-white p-2 hover:bg-gray-50' : 'flex max-w-full items-center'}`}
            >
                {milestoneContent}
                {inPopover && (
                    <div className='flex min-w-0 flex-col items-start gap-1'>
                        {isSamePerson ? (
                            <IconToolTip title={spName}>
                                <span className='max-w-full truncate rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-font09 font-medium text-indigo-600'>
                                    SP/FE&nbsp; {spName}
                                </span>
                            </IconToolTip>
                        ) : (
                            <>
                                <IconToolTip title={`SP: ${sp ? spName : 'Not Assigned'}`}>
                                    <span className={`max-w-full truncate rounded-full border px-2 py-0.5 text-font09 font-medium ${sp ? 'border-indigo-200 bg-indigo-50 text-indigo-600' : 'border-gray-200 bg-gray-50 text-gray-400'}`}>
                                        SP&nbsp; {sp ? spName : 'Not Assigned'}
                                    </span>
                                </IconToolTip>
                                <IconToolTip title={`FE: ${fe ? feName : 'Not Assigned'}`}>
                                    <span className={`max-w-full truncate rounded-full border px-2 py-0.5 text-font09 font-medium ${fe ? 'border-orange-200 bg-orange-50 text-orange-600' : 'border-gray-200 bg-gray-50 text-gray-400'}`}>
                                        FE&nbsp; {fe ? feName : 'Not Assigned'}
                                    </span>
                                </IconToolTip>
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const hiddenMilestoneCount = Math.max(0, milestones.length - visibleMilestoneCount);

    return (
        <div className='w-full '>
            <div className='flex flex-col gap-2 mb-1'>
                {milestones.slice(0, visibleMilestoneCount).map((milestone) => renderMilestone(milestone))}
                {hiddenMilestoneCount > 0 && (
                    <button
                        type='button'
                        aria-label={`Show all ${milestones.length} milestones`}
                        onClick={(event) => setMilestonesAnchorEl(event.currentTarget)}
                        className='w-max rounded-full border border-indigo-200 bg-indigo-50 px-2 py-1 text-font10 font-semibold text-indigo-600 shadow-sm transition-colors hover:border-indigo-300 hover:bg-indigo-100'
                    >
                        +{hiddenMilestoneCount} more
                    </button>
                )}
            </div>
            <Popover
                open={Boolean(milestonesAnchorEl)}
                anchorEl={milestonesAnchorEl}
                onClose={() => setMilestonesAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    className: 'mt-1 overflow-hidden rounded-xl border border-gray-200 shadow-xl',
                    style: { width: 'min(38rem, calc(100vw - 2rem))' }
                }}
            >
                <div className='border-b border-gray-100 bg-gray-50 px-4 py-3'>
                    <p className='text-font12 font-semibold tracking-wide text-gray-600'>ALL MILESTONES</p>
                    <p className='mt-0.5 text-font10 text-gray-400'>{milestones.length} milestones</p>
                </div>
                <div className='grid max-h-72 grid-cols-1 gap-2 overflow-y-auto p-3'>
                    {milestones.map((milestone) => renderMilestone(milestone, { inPopover: true }))}
                </div>
            </Popover>
            <div className='flex gap-1 mb-1 items-center'>
                <SimpleButton
                    buttonIconComponent={<span className='material-icons text-font13'>room_preferences</span>}
                    btnClass={'bg-white border flex items-center justify-center border-scogoprimary leading-6 cursor-pointer text-scogoprimary rounded-lg gap-1 font-medium cursor-pointer text-font09 hover:bg-scogoprimary hover:text-white max-w-max px-1'}
                    textOrComponent={'Execute'}
                    onClick={() => {
                        onOpenChatCLick({ ticketId: ticket_id, buttonName: 'Execute' });
                    }}
                    disabled={false}
                    loading={formButtonLoading['open_chat_' + ticket_id]}
                />
                {isCreateTicketAllowed(newLoggedUserData.actions) && ticket.status !== TicketStatus.CLOSED && ticket.status !== TicketStatus.ON_HOLD ? <IconToolTip title='Add New Milestone'>
                    <span onClick={onAddNewMilestoneClick} className='material-icons text-scogoorange cursor-pointer text-font13'>add</span>
                </IconToolTip> : null}
            </div>
        </div>
    );
};
